import React, { useEffect, useState } from 'react'
import { Box, CardMedia, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { LineItem } from 'src/Libs/shop/models'
import ClearIcon from '@mui/icons-material/Close'
import { currencyFormatter } from '../../utils/formatter'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useDebounce } from '@e3dc-react/shell/Hooks/useDebounce'
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton'
import { PRODUCT_CART_DEBOUNCE_TIMER } from '../../consts'
import { getImageUrlBySize } from '../../utils'
import { ProductType, ShopImageThumnbailSizes } from '../../types'
import ButtonQuantityGroup from '../ShopProduct/ButtonQuantityGroup'

interface ProductCheckoutOverviewProps {
  configurator: LineItem
  isLoading: boolean
  onUpdateProductCart: (productId: string, quantity: number) => void
  onDeleteProductCart: (productId: string) => void
}

const useStyles = makeStyles()(theme => ({
  productWrapper: {
    display: 'block',
    padding: `${theme.spacing(1)} 0`,
  },
  product: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      position: 'relative',
      paddingBottom: theme.spacing(10),
      borderBottom: `1px solid ${theme.palette.shades.shade03}`,
    },
  },
  productImage: {
    minWidth: '84px',
    maxWidth: '84px',
    objectFit: 'contain',
    height: '100%',
    alignSelf: 'flex-start',
  },
  productContent: {
    paddingLeft: theme.spacing(1),
    display: 'block',
    flex: 'auto',
  },
  productTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    width: '100%',
  },
  closeIconButton: {
    border: `1px solid ${theme.palette.shades.shade02}`,
    backgroundColor: theme.palette.shades.shade04,
    borderRadius: theme.shape.borderRadius,
    minWidth: '36px',
    minHeight: '36px',
    marginLeft: theme.spacing(1),
    padding: 0,
    color: theme.palette.buttonGroup.inactiveColor,
  },
  priceContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'flex-start',
    },
  },
  labelText: {
    paddingRight: theme.spacing(2),
    minWidth: '180px',
  },
}))

/*
 * This component shows normal Product to the Shop Cart Sidebar.
 * this also handle the Quantity change or Delete of product.
 */

const ProductCheckoutOverview: React.FC<ProductCheckoutOverviewProps> = ({ isLoading, configurator, onDeleteProductCart, onUpdateProductCart }) => {
  const { classes } = useStyles()
  const { t } = useCustomTranslation()
  const totalPrice = configurator.price.totalPrice
  const normalPrice = (configurator.price.listPrice && configurator.price.listPrice?.price * configurator.price.quantity) ?? 0
  const hasDiscount = normalPrice > totalPrice
  const [quantity, setQuantity] = useState<number>(configurator.quantity)
  const debouncedQuantity = useDebounce(quantity, PRODUCT_CART_DEBOUNCE_TIMER)
  const disableMinQuantity = configurator.quantityInformation.minPurchase
  const disableMaxQuantity = configurator.quantityInformation.maxPurchase
  const productImageUrl = getImageUrlBySize(configurator?.cover?.thumbnails, ShopImageThumnbailSizes.Small)
  const isProductTypeRetrofitBattery = configurator.payload.e3dc.productType === ProductType.RetrofitBattery
  const retrofitBatterySerialNumber = configurator.payload.e3dc?.serialNumber
  const removable = configurator.removable

  useEffect(() => {
    if (debouncedQuantity !== configurator.quantity) {
      onUpdateProductCart(configurator.id, debouncedQuantity)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuantity])

  return (
    <Box className={classes.productWrapper}>
      <Box className={classes.product}>
        <CardMedia className={classes.productImage} component="img" image={productImageUrl} />
        <Box className={classes.productContent}>
          <Box className={classes.productTitle}>
            <Box>
              <Typography className={classes.labelText}>{configurator.label}</Typography>
              {isProductTypeRetrofitBattery && retrofitBatterySerialNumber && (
                <Typography variant="body2" className={classes.labelText}>
                  {`${t('shop.system-configure-checkout.serial-number-label')}: ${retrofitBatterySerialNumber}`}
                </Typography>
              )}
            </Box>
            {removable && (
              <>
                <ButtonQuantityGroup
                  quantity={quantity}
                  setQuantity={quantity => {
                    setQuantity(quantity)
                  }}
                  minQuantity={disableMinQuantity}
                  maxQuantity={disableMaxQuantity}
                  isLoading={isLoading}
                />
                <LoadingButton
                  disabled={isLoading}
                  onClick={() => {
                    onDeleteProductCart(configurator.id)
                  }}
                  className={classes.closeIconButton}
                  size="small"
                >
                  <ClearIcon />
                </LoadingButton>
              </>
            )}
          </Box>

          <Box className={classes.priceContainer}>
            {hasDiscount && (
              <Typography variant="subtitle2" align="right" color="inherit" sx={{ marginRight: 2, textDecoration: 'line-through' }}>
                {currencyFormatter(normalPrice)}
              </Typography>
            )}
            <Typography variant="h6" align="right" color="primary">
              {currencyFormatter(totalPrice)}
            </Typography>
          </Box>
          {hasDiscount && (
            <Typography variant="h4" align="right" color="primary">
              {`${t('shop.system-configure-checkout.saved-price')}`}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default ProductCheckoutOverview
